import { Middleware, ResponseContext } from '../../api';
import { findContract, IContractEntry } from './contract.registry';
import { reportContractDrift } from './contract.reporter';
import { IContractIssue } from './contract.types';

/**
 * Response-boundary contract check.
 *
 * ## Why this is observe-only
 *
 * It would be easy to make this reject a response that fails its schema. That would be the
 * wrong call here. This is a support console used to work claims: if the API adds a coverage
 * type or renames a field we do not read, throwing would take the payers screen down for an
 * operator mid-task, to protect them from a difference that did not affect the work. The
 * failure mode of strict parsing is worse than the failure mode it prevents.
 *
 * So: the response is returned untouched, always. The check runs on a clone and its only
 * output is a report. That is the "observability" half of what was asked for; the "control"
 * half is that drift becomes a signal someone acts on, rather than an undefined discovered
 * in production three weeks later.
 *
 * ## Cost
 *
 * Only allow-listed paths are checked (see `contract.registry.ts`), and only successful
 * JSON responses. List payloads are sampled — see `MAX_RECORDS_CHECKED`. A drift signature
 * is reported once per session. Everything runs after the response has already been handed
 * back, so a slow check cannot delay a render.
 */

/**
 * Validating a full page of records would mean re-walking every row on every fetch, and the
 * Nth malformed record almost never says anything the first did not. A prefix is enough to
 * catch a shape change, which is what this is for.
 */
const MAX_RECORDS_CHECKED = 5;

/** Keep a drift report small enough to be readable in a Sentry event. */
const MAX_ISSUES_PER_DRIFT = 10;

function toIssues(error: { issues: Array<{ path: PropertyKey[]; code: string; message: string }> }): IContractIssue[] {
    return error.issues.map(issue => ({
        path: issue.path.map(String).join('.') || '(root)',
        code: issue.code,
        message: issue.message,
    }));
}

function checkPayload(entry: IContractEntry, path: string, payload: unknown): void {
    const isList = Array.isArray(payload);
    const records = isList ? (payload as unknown[]) : [payload];
    const checked = records.slice(0, MAX_RECORDS_CHECKED);

    const issues: IContractIssue[] = [];

    checked.forEach((record, index) => {
        const result = entry.schema.safeParse(record);

        if (result.success) {
            return;
        }

        const prefix = isList ? `[${index}].` : '';
        toIssues(result.error).forEach(issue => {
            issues.push({ ...issue, path: `${prefix}${issue.path}` });
        });
    });

    if (issues.length === 0) {
        return;
    }

    reportContractDrift({
        contract: entry.name,
        path,
        issues: issues.slice(0, MAX_ISSUES_PER_DRIFT),
        issueCount: issues.length,
        recordsChecked: checked.length,
        recordsTotal: records.length,
    });
}

/**
 * Builds the middleware. Wired in `configureCore` alongside auth and abort handling.
 */
export function createContractMiddleware(): Middleware {
    return {
        async post(context: ResponseContext): Promise<void> {
            try {
                const { response } = context;

                if (!response.ok) {
                    return;
                }

                const contentType = response.headers.get('content-type') || '';
                if (!contentType.includes('json')) {
                    return;
                }

                // Query strings carry filter values, which on this API include patient and
                // subscriber identifiers. Strip before anything is matched or reported —
                // a drift report ends up in telemetry, and telemetry is not a PHI sink.
                const path = context.url.split('?')[0];

                const entry = findContract(path);
                if (!entry) {
                    return;
                }

                // The caller has not read the body yet; cloning leaves the original stream
                // intact. Reading `response` directly here would break every consumer.
                const payload = await response.clone().json();

                checkPayload(entry, path, payload);
            } catch {
                // Observability must not be able to fail a request. A malformed body, an
                // already-consumed stream, a schema bug — all of it is swallowed here on
                // purpose. Returning void leaves the real response untouched.
            }
        },
    };
}
